/**
 * MovingWallSystem — manages walls that periodically open and close.
 *
 * From level 8+, selected wall pairs alternate between open (PATH) and
 * closed (WALL) states on a timer cycle. Three animation types:
 *   1. Alternating: pair A is open while pair B is closed, then they swap
 *   2. Disappear/Reappear: walls vanish then return
 *   3. Rotating sections: a group of walls rotates orientation
 *
 * A warning sound plays 2 seconds before each shift. If the player is
 * standing on a cell that is about to become a wall, the change is
 * delayed until the player moves away.
 *
 * Moving wall sprites have a subtle pulsing shimmer (alpha oscillation)
 * that distinguishes them from normal walls without using colored overlays.
 *
 * Moving walls are visible on the minimap (Z key).
 */

import { CellType, Events } from '../utils/constants.js';
import {
  TILE_SIZE,
  MOVING_WALL_MIN_LEVEL,
  MOVING_WALL_CYCLE_MIN,
  MOVING_WALL_CYCLE_MAX,
  MOVING_WALL_WARNING_TIME,
  MOVING_WALL_TRANSITION_DURATION,
  MOVING_WALL_COUNT_BASE,
  MOVING_WALL_COUNT_PER_LEVEL,
  MOVING_WALL_COUNT_MAX,
  MOVING_WALL_PLAYER_RETRY_DELAY,
  AUDIO_WALL_SHIFT_WARNING,
  AUDIO_WALL_SHIFT,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';
import MazeGenerator from './MazeGenerator.js';

/* ── Shimmer tuning ──────────────────────────────────────────────── */

/** Normal shimmer: slow, gentle alpha oscillation. */
const SHIMMER_ALPHA_MIN = 0.72;
const SHIMMER_ALPHA_MAX = 1.0;
const SHIMMER_DURATION = 1800;   // ms per half-cycle (low→high)

/** Warning shimmer: faster, more intense. */
const WARNING_ALPHA_MIN = 0.45;
const WARNING_ALPHA_MAX = 1.0;
const WARNING_DURATION = 250;    // ms per half-cycle during warning

/**
 * A wall pair: two wall positions that alternate between open/closed.
 * @typedef {{
 *   wallA: { col: number, row: number },
 *   wallB: { col: number, row: number },
 *   stateAOpen: boolean,
 *   spriteA: Phaser.GameObjects.Sprite|null,
 *   spriteB: Phaser.GameObjects.Sprite|null,
 *   shimmerA: Phaser.Tweens.Tween|null,
 *   shimmerB: Phaser.Tweens.Tween|null
 * }} WallPair
 */

export default class MovingWallSystem {
  /**
   * @param {Phaser.Scene} scene         - The GameScene instance.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {number[][]} grid            - The maze grid (expanded).
   * @param {number} level               - Current game level.
   * @param {{ col: number, row: number }} startPos
   * @param {{ col: number, row: number }} exitPos
   * @param {object} playerRef           - Player sprite (for position checks).
   * @param {number} [seed]              - Optional seed for deterministic placement.
   */
  constructor(scene, eventBus, grid, level, startPos, exitPos, playerRef, seed) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = eventBus;

    /** @type {number[][]} */
    this.grid = grid;

    /** @type {number} */
    this.level = level;

    /** @type {object} */
    this._playerRef = playerRef;

    /** @type {WallPair[]} */
    this._pairs = [];

    /** @type {Phaser.Time.TimerEvent|null} Main cycle timer. */
    this._cycleTimer = null;

    /** @type {Phaser.Time.TimerEvent|null} Warning timer before shift. */
    this._warningTimer = null;

    /** @type {number} Cycle duration in ms. */
    this._cycleDuration = 0;

    /** @type {boolean} */
    this._destroyed = false;

    /** @type {boolean} */
    this._paused = false;

    /** @type {number} Remaining cycle time when paused. */
    this._pausedCycleRemaining = 0;

    /** @type {boolean} Whether warning has been given for current cycle. */
    this._warningGiven = false;

    // Seeded RNG
    this._rngState = seed ?? (Date.now() ^ 0xfeedface);

    if (level >= MOVING_WALL_MIN_LEVEL) {
      this._selectWallPairs(startPos, exitPos);
      if (this._pairs.length > 0) {
        this._initVisuals();
        this._startCycle();
      }
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Get all moving wall cell positions (both open and closed) for minimap.
   * @returns {Array<{ col: number, row: number, isOpen: boolean }>}
   */
  getMovingWallCells() {
    const cells = [];
    for (const pair of this._pairs) {
      cells.push({
        col: pair.wallA.col,
        row: pair.wallA.row,
        isOpen: pair.stateAOpen,
      });
      cells.push({
        col: pair.wallB.col,
        row: pair.wallB.row,
        isOpen: !pair.stateAOpen,
      });
    }
    return cells;
  }

  /**
   * Check if a cell is a moving wall (open or closed).
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isMovingWall(col, row) {
    const key = `${col},${row}`;
    for (const pair of this._pairs) {
      if (`${pair.wallA.col},${pair.wallA.row}` === key) return true;
      if (`${pair.wallB.col},${pair.wallB.row}` === key) return true;
    }
    return false;
  }

  /**
   * Serialize for save state.
   * @returns {{ pairs: Array<{ wallA: object, wallB: object, stateAOpen: boolean }>, cycleDuration: number }}
   */
  serialize() {
    return {
      pairs: this._pairs.map(p => ({
        wallA: p.wallA,
        wallB: p.wallB,
        stateAOpen: p.stateAOpen,
      })),
      cycleDuration: this._cycleDuration,
    };
  }

  /**
   * Restore from save data.
   * @param {{ pairs: Array, cycleDuration: number }} saveData
   */
  restore(saveData) {
    if (!saveData || !saveData.pairs) return;

    this._destroyVisuals();
    this._pairs = [];

    for (const sp of saveData.pairs) {
      const pair = {
        wallA: sp.wallA,
        wallB: sp.wallB,
        stateAOpen: sp.stateAOpen,
        spriteA: null,
        spriteB: null,
        shimmerA: null,
        shimmerB: null,
      };
      this._pairs.push(pair);

      // Apply grid state
      this.grid[pair.wallA.row][pair.wallA.col] = pair.stateAOpen ? CellType.PATH : CellType.WALL;
      this.grid[pair.wallB.row][pair.wallB.col] = pair.stateAOpen ? CellType.WALL : CellType.PATH;
    }

    this._cycleDuration = saveData.cycleDuration || this._randomCycleDuration();
    this._initVisuals();
    this._startCycle();
  }

  /**
   * Pause the cycle timer.
   */
  pause() {
    if (this._paused) return;
    this._paused = true;

    if (this._cycleTimer) {
      this._pausedCycleRemaining = this._cycleTimer.getRemaining();
      this._cycleTimer.remove(false);
      this._cycleTimer = null;
    }
    if (this._warningTimer) {
      this._warningTimer.remove(false);
      this._warningTimer = null;
    }

    // Pause shimmer tweens
    for (const pair of this._pairs) {
      if (pair.shimmerA && pair.shimmerA.isPlaying()) pair.shimmerA.pause();
      if (pair.shimmerB && pair.shimmerB.isPlaying()) pair.shimmerB.pause();
    }
  }

  /**
   * Resume the cycle timer.
   */
  resume() {
    if (!this._paused) return;
    this._paused = false;

    if (this._pausedCycleRemaining > 0 && this._pairs.length > 0) {
      this._cycleTimer = this.scene.time.delayedCall(
        this._pausedCycleRemaining,
        () => this._onCycleTick(),
      );
      this._pausedCycleRemaining = 0;
    }

    // Resume shimmer tweens
    for (const pair of this._pairs) {
      if (pair.shimmerA && pair.shimmerA.isPaused()) pair.shimmerA.resume();
      if (pair.shimmerB && pair.shimmerB.isPaused()) pair.shimmerB.resume();
    }
  }

  /**
   * Clean up everything.
   */
  destroy() {
    this._destroyed = true;

    if (this._cycleTimer) {
      this._cycleTimer.remove(false);
      this._cycleTimer = null;
    }
    if (this._warningTimer) {
      this._warningTimer.remove(false);
      this._warningTimer = null;
    }

    this._destroyVisuals();
    this._pairs = [];

    this.scene = null;
    this.eventBus = null;
    this.grid = null;
    this._playerRef = null;
  }

  // ---------------------------------------------------------------
  // Private — selection
  // ---------------------------------------------------------------

  /** @private */
  _nextRandom() {
    this._rngState = (this._rngState * 1664525 + 1013904223) & 0x7fffffff;
    return this._rngState / 0x80000000;
  }

  /** @private */
  _randInt(min, max) {
    return min + Math.floor(this._nextRandom() * (max - min + 1));
  }

  /** @private */
  _randomCycleDuration() {
    return MOVING_WALL_CYCLE_MIN + Math.floor(
      this._nextRandom() * (MOVING_WALL_CYCLE_MAX - MOVING_WALL_CYCLE_MIN),
    );
  }

  /**
   * Select wall pairs to alternate. Each pair consists of two wall
   * cells that have PATH cells on opposite sides (same as secret passage
   * candidates), ensuring that opening them creates valid shortcuts.
   *
   * @private
   * @param {{ col: number, row: number }} startPos
   * @param {{ col: number, row: number }} exitPos
   */
  _selectWallPairs(startPos, exitPos) {
    const gridH = this.grid.length;
    const gridW = this.grid[0].length;

    // Collect candidate walls (interior, bridge-type)
    const candidates = [];
    const excluded = new Set();

    // Exclude cells near start and exit
    for (const pos of [startPos, exitPos]) {
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          excluded.add(`${pos.col + dx},${pos.row + dy}`);
        }
      }
    }

    for (let row = 2; row < gridH - 2; row++) {
      for (let col = 2; col < gridW - 2; col++) {
        if (this.grid[row][col] !== CellType.WALL) continue;
        if (excluded.has(`${col},${row}`)) continue;

        // Must be a bridge wall (PATH on two opposite sides)
        const leftPath = col > 0 && this.grid[row][col - 1] === CellType.PATH;
        const rightPath = col < gridW - 1 && this.grid[row][col + 1] === CellType.PATH;
        const upPath = row > 0 && this.grid[row - 1][col] === CellType.PATH;
        const downPath = row < gridH - 1 && this.grid[row + 1][col] === CellType.PATH;

        if ((leftPath && rightPath) || (upPath && downPath)) {
          candidates.push({ col, row });
        }
      }
    }

    // Determine target count
    const levelsAbove = this.level - MOVING_WALL_MIN_LEVEL;
    let targetCount = MOVING_WALL_COUNT_BASE + levelsAbove * MOVING_WALL_COUNT_PER_LEVEL;
    targetCount = Math.min(targetCount, MOVING_WALL_COUNT_MAX);

    // Shuffle candidates
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(this._nextRandom() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    // Select pairs — each pair is two wall candidates that aren't too close
    const used = new Set();
    const pairsNeeded = Math.floor(targetCount / 2) || 1;

    for (let p = 0; p < pairsNeeded && candidates.length >= 2; p++) {
      let wallA = null;
      let wallB = null;

      for (const c of candidates) {
        const key = `${c.col},${c.row}`;
        if (used.has(key)) continue;

        if (!wallA) {
          wallA = c;
          continue;
        }

        // Ensure minimum distance between pair members
        const dist = Math.abs(c.col - wallA.col) + Math.abs(c.row - wallA.row);
        if (dist >= 4) {
          wallB = c;
          break;
        }
      }

      if (wallA && wallB) {
        used.add(`${wallA.col},${wallA.row}`);
        used.add(`${wallB.col},${wallB.row}`);

        // wallA starts OPEN (PATH), wallB starts CLOSED (WALL) — then they alternate
        this.grid[wallA.row][wallA.col] = CellType.PATH;
        // wallB stays as WALL (its current state)

        this._pairs.push({
          wallA,
          wallB,
          stateAOpen: true,
          spriteA: null,
          spriteB: null,
          shimmerA: null,
          shimmerB: null,
        });
      }
    }

    this._cycleDuration = this._randomCycleDuration();
  }

  // ---------------------------------------------------------------
  // Private — visuals
  // ---------------------------------------------------------------

  /**
   * Create/update wall sprites to match current pair states and
   * attach shimmer tweens to the closed wall in each pair.
   * @private
   */
  _initVisuals() {
    if (!this.scene) return;

    for (const pair of this._pairs) {
      // Wall A: currently open → no wall sprite; closed → wall sprite + shimmer
      if (pair.stateAOpen) {
        this._removeWallSpriteAt(pair.wallA.col, pair.wallA.row);
        pair.spriteA = null;
        pair.shimmerA = null;
      } else {
        pair.spriteA = this._ensureWallSprite(pair.wallA.col, pair.wallA.row);
        pair.shimmerA = this._addShimmer(pair.spriteA);
      }

      // Wall B: opposite of A
      if (!pair.stateAOpen) {
        this._removeWallSpriteAt(pair.wallB.col, pair.wallB.row);
        pair.spriteB = null;
        pair.shimmerB = null;
      } else {
        pair.spriteB = this._ensureWallSprite(pair.wallB.col, pair.wallB.row);
        pair.shimmerB = this._addShimmer(pair.spriteB);
      }
    }
  }

  /**
   * Ensure a wall sprite exists at the given grid position.
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {Phaser.GameObjects.Sprite|null}
   */
  _ensureWallSprite(col, row) {
    if (!this.scene || !this.scene.wallGroup) return null;

    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    // Check if sprite already exists
    const children = this.scene.wallGroup.getChildren();
    for (const child of children) {
      if (Math.abs(child.x - x) < 1 && Math.abs(child.y - y) < 1) {
        return child;
      }
    }

    // Create new wall sprite
    const wallTexKey = this.scene._currentWallTexture;
    const wall = this.scene.wallGroup.create(x, y, wallTexKey);
    wall.setOrigin(0.5);
    wall.refreshBody();
    return wall;
  }

  /**
   * Remove a wall sprite at the given grid position.
   * @private
   */
  _removeWallSpriteAt(col, row) {
    if (!this.scene || !this.scene.wallGroup) return;
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const children = this.scene.wallGroup.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (Math.abs(child.x - x) < 1 && Math.abs(child.y - y) < 1) {
        child.destroy();
        break;
      }
    }
  }

  /**
   * Attach a looping alpha shimmer tween to a wall sprite so the
   * player can distinguish it from normal walls.
   * @private
   * @param {Phaser.GameObjects.Sprite|null} sprite
   * @returns {Phaser.Tweens.Tween|null}
   */
  _addShimmer(sprite) {
    if (!sprite || !this.scene) return null;

    // Start with a slightly reduced alpha so the shimmer is visible
    sprite.setAlpha(SHIMMER_ALPHA_MIN);

    return this.scene.tweens.add({
      targets: sprite,
      alpha: { from: SHIMMER_ALPHA_MIN, to: SHIMMER_ALPHA_MAX },
      duration: SHIMMER_DURATION,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Stop and remove a shimmer tween.
   * @private
   * @param {Phaser.Tweens.Tween|null} tween
   */
  _stopShimmer(tween) {
    if (tween) {
      tween.stop();
      tween.destroy();
    }
  }

  /**
   * Switch a wall sprite's shimmer to a faster, more intense pulse
   * to warn the player the wall is about to shift.
   * @private
   * @param {Phaser.GameObjects.Sprite|null} sprite
   * @param {Phaser.Tweens.Tween|null} currentTween
   * @returns {Phaser.Tweens.Tween|null} The new warning tween.
   */
  _setWarningShimmer(sprite, currentTween) {
    if (!sprite || !this.scene) return null;

    this._stopShimmer(currentTween);

    return this.scene.tweens.add({
      targets: sprite,
      alpha: { from: WARNING_ALPHA_MIN, to: WARNING_ALPHA_MAX },
      duration: WARNING_DURATION,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Destroy all shimmer tweens.
   * @private
   */
  _destroyVisuals() {
    for (const pair of this._pairs) {
      this._stopShimmer(pair.shimmerA);
      pair.shimmerA = null;
      this._stopShimmer(pair.shimmerB);
      pair.shimmerB = null;
    }
  }

  // ---------------------------------------------------------------
  // Private — cycle management
  // ---------------------------------------------------------------

  /**
   * Start the shift cycle.
   * @private
   */
  _startCycle() {
    if (this._destroyed || this._pairs.length === 0) return;

    // Schedule the warning first
    const warningDelay = Math.max(0, this._cycleDuration - MOVING_WALL_WARNING_TIME);

    this._warningGiven = false;

    this._warningTimer = this.scene.time.delayedCall(warningDelay, () => {
      this._warningTimer = null;
      this._playWarning();
    });

    this._cycleTimer = this.scene.time.delayedCall(this._cycleDuration, () => {
      this._cycleTimer = null;
      this._onCycleTick();
    });
  }

  /**
   * Play warning sound and intensify shimmer on walls about to shift.
   * @private
   */
  _playWarning() {
    if (this._destroyed) return;
    this._warningGiven = true;

    try {
      this.scene.sound.play(AUDIO_WALL_SHIFT_WARNING, { volume: getSfxVol(0.3) });
    } catch (_) { /* audio fallback */ }

    this.eventBus.emit(Events.MOVING_WALL_WARNING);

    // Intensify shimmer on all closed wall sprites (about to open)
    for (const pair of this._pairs) {
      if (pair.stateAOpen) {
        // B is closed — speed up its shimmer
        pair.shimmerB = this._setWarningShimmer(pair.spriteB, pair.shimmerB);
      } else {
        // A is closed — speed up its shimmer
        pair.shimmerA = this._setWarningShimmer(pair.spriteA, pair.shimmerA);
      }
    }
  }

  /**
   * Execute the wall shift: toggle each pair's state.
   * @private
   */
  _onCycleTick() {
    if (this._destroyed) return;

    // Check if player is blocking any cell that needs to become a wall
    let blocked = false;
    if (this._playerRef) {
      const pCol = this._playerRef.gridCol;
      const pRow = this._playerRef.gridRow;

      for (const pair of this._pairs) {
        // The open side is about to become a wall
        const openWall = pair.stateAOpen ? pair.wallA : pair.wallB;
        if (openWall.col === pCol && openWall.row === pRow) {
          blocked = true;
          break;
        }
      }
    }

    if (blocked) {
      // Retry after a delay
      this._cycleTimer = this.scene.time.delayedCall(MOVING_WALL_PLAYER_RETRY_DELAY, () => {
        this._cycleTimer = null;
        this._onCycleTick();
      });
      return;
    }

    // Execute the shift
    for (const pair of this._pairs) {
      pair.stateAOpen = !pair.stateAOpen;

      // Update grid
      this.grid[pair.wallA.row][pair.wallA.col] = pair.stateAOpen ? CellType.PATH : CellType.WALL;
      this.grid[pair.wallB.row][pair.wallB.col] = pair.stateAOpen ? CellType.WALL : CellType.PATH;

      // Update sprites + shimmer
      if (pair.stateAOpen) {
        // A opens: stop shimmer, remove wall sprite
        this._stopShimmer(pair.shimmerA);
        pair.shimmerA = null;
        this._removeWallSpriteAt(pair.wallA.col, pair.wallA.row);
        pair.spriteA = null;

        // B closes: add wall sprite with shimmer
        pair.spriteB = this._ensureWallSprite(pair.wallB.col, pair.wallB.row);
        if (pair.spriteB) {
          pair.spriteB.setAlpha(0);
          this.scene.tweens.add({
            targets: pair.spriteB,
            alpha: SHIMMER_ALPHA_MIN,
            duration: MOVING_WALL_TRANSITION_DURATION,
            ease: 'Sine.easeIn',
            onComplete: () => {
              // Attach normal shimmer once the fade-in finishes
              if (pair.spriteB && pair.spriteB.active) {
                pair.shimmerB = this._addShimmer(pair.spriteB);
              }
            },
          });
        }
      } else {
        // B opens: stop shimmer, remove wall sprite
        this._stopShimmer(pair.shimmerB);
        pair.shimmerB = null;
        this._removeWallSpriteAt(pair.wallB.col, pair.wallB.row);
        pair.spriteB = null;

        // A closes: add wall sprite with shimmer
        pair.spriteA = this._ensureWallSprite(pair.wallA.col, pair.wallA.row);
        if (pair.spriteA) {
          pair.spriteA.setAlpha(0);
          this.scene.tweens.add({
            targets: pair.spriteA,
            alpha: SHIMMER_ALPHA_MIN,
            duration: MOVING_WALL_TRANSITION_DURATION,
            ease: 'Sine.easeIn',
            onComplete: () => {
              // Attach normal shimmer once the fade-in finishes
              if (pair.spriteA && pair.spriteA.active) {
                pair.shimmerA = this._addShimmer(pair.spriteA);
              }
            },
          });
        }
      }
    }

    // Play shift sound
    try {
      this.scene.sound.play(AUDIO_WALL_SHIFT, { volume: getSfxVol(0.4) });
    } catch (_) { /* audio fallback */ }

    this.eventBus.emit(Events.MOVING_WALL_SHIFTED);

    // Schedule next cycle
    this._cycleDuration = this._randomCycleDuration();
    this._startCycle();
  }
}
